import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auth, errorMessage, setSessionExpiredHandler, tokens } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const signOutLocally = useCallback(() => {
    tokens.clear();
    setUser(null);
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(signOutLocally);
  }, [signOutLocally]);

  useEffect(() => {
    if (!tokens.access) {
      setLoading(false);
      return;
    }
    auth
      .me()
      .then(({ data }) => setUser(data))
      .catch(signOutLocally)
      .finally(() => setLoading(false));
  }, [signOutLocally]);

  const login = useCallback(async (username, password) => {
    try {
      const { data } = await auth.login(username, password);
      tokens.set({ access: data.access, refresh: data.refresh });
      setUser(data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: errorMessage(error, 'نام کاربری یا رمز عبور نادرست است.') };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      // توکن ممکن است از قبل باطل شده باشد؛ خروج محلی در هر حال انجام می‌شود.
    }
    signOutLocally();
  }, [signOutLocally]);

  /** بعد از عملیاتی که پروفایل را عوض می‌کند (مثلاً اتصال تلگرام)، دوباره می‌خواند. */
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await auth.me();
      setUser(data);
    } catch {
      // بی‌اهمیت — نسخه‌ی فعلیِ حافظه همچنان معتبر است.
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser, role: user?.role ?? null }),
    [user, loading, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth باید داخل AuthProvider استفاده شود.');
  return context;
}
